import usePageMeta from '@/hooks/usePageMeta';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import ReportForm from '@/pages/contact/components/ReportForm';
import { contactInfo } from '@/mocks/site';

const guideList = [
  '尽量写清时间、平台类型与具体现象，越具体越容易核实。',
  '如有截图、短信或扣款记录，可在提交后按邮件回复指引补充。',
  '请不要在描述中填写完整账号、密码或身份证号等敏感信息。',
  '报料可匿名处理，我们不会公开你的邮箱等可识别身份的信息。',
];

export default function Contact() {
  usePageMeta({
    title: '报料与联系｜提交网站安全事件线索 - 黑料雷达',
    description:
      '向黑料雷达提交网站安全事件线索、更正申请或合作咨询。填写报料表单即可匿名提交，我们会在 48 小时内查看并视情况通过邮箱联系核实。',
    keywords: '报料,线索提交,安全事件报料,内容更正,联系方式',
    canonical: '/contact',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '报料与联系',
        url: 'https://worswgu.cn/contact',
        inLanguage: 'zh-CN',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://worswgu.cn/' },
            { '@type': 'ListItem', position: 2, name: '报料与联系', item: 'https://worswgu.cn/contact' },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '黑料雷达',
        url: 'https://worswgu.cn/',
        email: contactInfo.email,
      },
    ],
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '报料与联系' }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            报料与联系
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-600">
            你遇到的情况，可能就是别人正在踩的坑。提交线索可以是匿名的，
            我们只核实信息本身，不追溯提供者身份。如果你是被提及的一方，也可以通过这里提交更正申请。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.5fr_1fr] lg:gap-14">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">在线报料表单</h2>
            <p className="mt-2 text-sm leading-7 text-foreground-600">
              带 <span className="text-primary-600">*</span> 为必填项。描述请控制在 500 字以内。
            </p>
            <div className="mt-5">
              <ReportForm />
            </div>
          </div>

          <aside className="min-w-0">
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">报料须知</h2>
              <ul className="mt-4 space-y-3">
                {guideList.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm leading-6 text-foreground-700">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-600">
                      <i className="ri-checkbox-circle-line text-base"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">直接联系我们</h2>
              <ul className="mt-4 space-y-4 text-sm text-foreground-700">
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-send-line text-base"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">线索报料</span>
                    <a href={`mailto:${contactInfo.email}`} className="text-foreground-800 hover:text-primary-700">
                      {contactInfo.email}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className="ri-mail-line text-base"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">更正与合作</span>
                    <a
                      href={`mailto:${contactInfo.cooperativeEmail}`}
                      className="text-foreground-800 hover:text-primary-700"
                    >
                      {contactInfo.cooperativeEmail}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className="ri-time-line text-base"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">处理时间</span>
                    <span className="text-foreground-800">{contactInfo.workingHours}</span>
                  </span>
                </li>
              </ul>
              <p className="mt-4 border-t border-background-200 pt-4 text-xs leading-6 text-foreground-500">
                {contactInfo.responseTime}。请勿通过邮件发送密码、验证码或身份证照片。
              </p>
            </div>

            <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-accent-900">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-alert-line text-base"></i>
                </span>
                紧急情况优先自身处理
              </h2>
              <p className="mt-2 text-xs leading-6 text-foreground-700">
                如果你的资金正在被盗刷或账号正被异常操作，请先联系银行冻结卡片、修改密码并报警，
                再回来向我们提交线索。处理时效比投稿更重要。
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}